import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { catchError, EMPTY, map, Observable, Subscription, tap } from "rxjs";
import { Cryptocoin } from "@models";
import { containsString } from "@helpers/utils";
import { CoreModule } from "@modules/core/core.module";
import { ModalService } from "@core-services/modal.service";
import { CryptocoinService } from "@main-services/currency";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { AddressBookWalletFacade } from "../services/address-book-wallet.facade";
import { AddressBookWalletService } from "../services/address-book-wallet.service";
import {
	AddressBookWalletHostExtra,
	AddressBookWalletHostModel,
	AddressBookWalletHostType,
	AddressBookWalletModel,
	AddressBookWalletModelAsset,
	AddressBookWalletModelNetwork,
	AddressBookWalletPayload,
	AddressBookWalletViewType
} from "../model/address-book-wallet.model";
import { NewProfileFacade } from "../../profile/new-profile/state/new-profile.facade";
import { ProfileModel } from "../../profile/new-profile/entities/profile-model";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { config } from "@config";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { SegmentService } from "@main-services/segment.service";
import * as levenshtein from "damerau-levenshtein";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "create-wallet-modal",
	templateUrl: "./address-book-create-wallet-modal.component.html",
	styleUrls: ["./address-book-create-wallet-modal.component.scss"],
	providers: [NewProfileFacade],
	imports: [CoreModule, ReactiveFormsModule]
})
export class AddressBookCreateWalletModalComponent implements OnInit, OnDestroy {
	@Input() set data({ type, wallet, accountWalletId, selectedNetwork, selectedCryptocoin }) {
		this.modalType = type;
		this.accountWalletId = accountWalletId;
		this.withdrawSelectedNetwork = selectedNetwork;
		this.withdrawSelectedCryptocoin = selectedCryptocoin;
		switch (type) {
			case AddressBookWalletViewType.LEGACY:
				this.handleLegacyMode(wallet);
				break;
			case AddressBookWalletViewType.NEW:
				this.handleNewMode();
				break;
			case AddressBookWalletViewType.WITHDRAW:
				if (wallet.draft) {
					this.isOriginalWalletDraft = true;
					this.selectedNetwork = selectedNetwork;
					this.selectedCryptocoin = selectedCryptocoin;
					this.handleLegacyMode(wallet);
				} else {
					this.handleWithdrawMode(wallet);
				}
				break;
			default:
				break;
		}
	}
	public assetOptions$: Observable<BpSelectOption[]> = EMPTY;
	public networkOptions$: Observable<BpSelectOption[]> = EMPTY;
	public walletHosts$: Observable<BpSelectOption[]> = EMPTY;
	public shouldDisplayDestinationTag: boolean;
	public destinationTagText: string;
	public destinationTagTextOptional: string;
	public modalTitle: string = "new_wallet";
	public modalDescription: string = "save_wallet_for_future_use";
	public modalType: AddressBookWalletViewType = AddressBookWalletViewType.NEW;
	public addressBookWalletViewType = AddressBookWalletViewType;
	public learnMoreUrl: string = config.externalPaths["travel_rule"]["faq"];
	public selectedNetwork: AddressBookWalletModelNetwork = null;
	public selectedCryptocoin: AddressBookWalletModelAsset = null;
	public shouldDisplayEmptyTagInfo: boolean = false;
	public walletAddressError: string;
	public hostHintError: string;
	public walletOwnerError: string;
	public destinationTagError: string;
	private accountWalletId?: string;
	private isOriginalWalletDraft: boolean;
	private selectedWalletHostId: string;

	public newWalletForm: FormGroup = new FormGroup({
		walletOwner: new FormControl<string>("", Validators.required),
		walletAddress: new FormControl<string>("", Validators.required),
		asset: new FormControl<string>("", Validators.required),
		network: new FormControl<string>("", Validators.required),
		walletHost: new FormControl<string>("", Validators.required),
		otherHostName: new FormControl<string>(""),
		ownWallet: new FormControl<boolean>(false),
		destinationTag: new FormControl<string>(""),
		selfDeclared: new FormControl<boolean>(false)
	});

	public get walletOwner(): AbstractControl<string> {
		return this.newWalletForm.get("walletOwner");
	}

	public get asset(): AbstractControl<string> {
		return this.newWalletForm.get("asset");
	}

	public get network(): AbstractControl<string> {
		return this.newWalletForm.get("network");
	}

	public get walletHost(): AbstractControl<string> {
		return this.newWalletForm.get("walletHost");
	}

	private get ownWallet(): AbstractControl<boolean> {
		return this.newWalletForm.get("ownWallet");
	}

	public get selfDeclared(): AbstractControl<boolean> {
		return this.newWalletForm.get("selfDeclared");
	}

	public get destinationTag(): AbstractControl<string> {
		return this.newWalletForm.get("destinationTag");
	}

	public get walletAddress(): AbstractControl<string> {
		return this.newWalletForm.get("walletAddress");
	}

	public get otherHostName(): AbstractControl<string> {
		return this.newWalletForm.get("otherHostName");
	}

	public get missingTagModalTitle(): string {
		return this.translationService.translate("missing_tag_modal_title", [this.destinationTagText]);
	}

	public get missingTagModalDescription(): string {
		return this.translationService.translate("missing_tag_modal_description", [
			this.destinationTagText.toLocaleLowerCase()
		]);
	}

	public get missingTagModalButton(): string {
		return this.translationService.translate("missing_tag_modal_button", [
			this.destinationTagText.toLocaleLowerCase()
		]);
	}

	private profileNameSubscription: Subscription = Subscription.EMPTY;
	private addNewWalletSubscription: Subscription = Subscription.EMPTY;
	private filterHostsSubscription: Subscription = Subscription.EMPTY;
	private profileName: string = "";
	private selectedAssetNetworks: BpSelectOption[] = [];
	private walletId: string;
	private withdrawSelectedNetwork: Network;
	private withdrawSelectedCryptocoin: PaymentsCryptocoin;

	protected readonly walletFacade = inject(AddressBookWalletFacade);
	protected readonly AddressBookWalletViewType = AddressBookWalletViewType;

	constructor(
		private readonly modalService: ModalService,
		private readonly cryptocoinService: CryptocoinService,
		private readonly walletService: AddressBookWalletService,
		private readonly profileFacade: NewProfileFacade,
		private readonly translationService: TranslationService,
		private readonly infoService: InfoService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService
	) {}

	ngOnInit(): void {
		this.assetOptions$ = this.walletFacade.getAssetOptions();
		this.walletHosts$ = this.walletFacade.getWalletHostsOptions().pipe(
			map((hosts: BpSelectOption<AddressBookWalletHostModel>[]) => [
				{
					name: this.translationService.translate("self_hosted_wallet"),
					value: AddressBookWalletHostExtra.SELF
				},
				...hosts,
				{
					name: this.translationService.translate("other_host"),
					value: AddressBookWalletHostExtra.OTHER
				}
			])
		);

		this.profileNameSubscription = this.profileFacade
			.getProfile$()
			.subscribe(({ fullName }: ProfileModel) => (this.profileName = fullName));

		this.segmentService.track({
			name: "Address Book Add Wallet Information Screen Viewed"
		});
		this.rudderstackService.track({
			name: "Address Book Add Wallet Information Screen Viewed"
		});
	}

	public onAssetChange(selectedAssetValue: string): void {
		this.networkOptions$ = this.walletFacade.getNetworksForSelectedCoin(selectedAssetValue).pipe(
			tap((networks: BpSelectOption[]) => {
				this.selectedAssetNetworks = networks;
				if (this.selectedAssetNetworks.length === 1) {
					this.onNetworkChange(this.selectedAssetNetworks[0].value);
				}
			})
		);
		this.asset.setValue(selectedAssetValue);
		this.shouldDisplayDestinationTag = false;
		this.walletAddress.setErrors(null);
	}

	public onNetworkChange(selectedNetworkValue: string): void {
		this.network.setValue(selectedNetworkValue);
		this.handleDestinationTagDisplay(selectedNetworkValue);
		this.walletAddress.setErrors(null);
	}

	public onWalletHostChange(selectedWalletHost: string): void {
		this.filterHostsSubscription = this.walletHosts$.subscribe({
			next: (hosts) => {
				let updatedWalletHostId = null;
				for (const host of hosts) {
					if (host.name === selectedWalletHost) {
						updatedWalletHostId = host?.["wallet_host_id"];
						break;
					}
				}
				this.selectedWalletHostId = updatedWalletHostId;
			}
		});
		this.walletHost.setValue(selectedWalletHost);
	}

	public onOwnWalletChange(isOwnWallet: boolean): void {
		this.ownWallet.setValue(isOwnWallet);
		if (isOwnWallet) {
			this.walletOwner.disable();
			this.walletOwner.setValue(this.profileName);
		} else {
			this.walletOwner.enable();
			this.walletOwner.reset();
		}
	}

	public onSelfDeclaredChange(selfDeclared: boolean): void {
		this.selfDeclared.setValue(selfDeclared);
	}

	public back(): void {
		if (this.shouldDisplayEmptyTagInfo) {
			this.shouldDisplayEmptyTagInfo = false;
		} else {
			this.cancel();
		}
	}

	public cancel(): void {
		this.modalService.pop();
	}

	public saveWallet(): void {
		this.handleAddingNewWallet();
	}

	public getCustomSearch(): Function {
		return (searchInput: string, { details: id }: BpSelectOption) => {
			const coin: Cryptocoin = this.getCoinFromFieldId(id);

			if (!coin) {
				return false;
			}
			if (containsString(coin.getSymbol(), searchInput)) {
				return true;
			}
			return containsString(coin.getName(), searchInput);
		};
	}

	public deleteWallet(): void {
		this.walletFacade.deleteWalletById(this.walletId);
	}

	public getHostSearch(): Function {
		return (searchInput: string, { name, value }: BpSelectOption): boolean => {
			const option: string = name.toLocaleLowerCase();
			const input: string = searchInput.toLocaleLowerCase();
			const lev = levenshtein(option, input);
			return (
				(lev.steps < 7 && lev.similarity > 0.49) ||
				option.includes(input) ||
				Object.values(AddressBookWalletHostExtra).includes(value)
			);
		};
	}

	private getCoinFromFieldId(id: string): Cryptocoin {
		return this.cryptocoinService.getItemById(id);
	}

	private handleDestinationTagDisplay(selectedNetworkValue: string, destinationTag?: string): void {
		let selectedNetworkDestinationTag = "";
		if (destinationTag) {
			selectedNetworkDestinationTag = destinationTag;
		} else {
			const selectedNetwork: BpSelectOption = this.selectedAssetNetworks.find(
				({ value }: BpSelectOption) => value === selectedNetworkValue
			);
			selectedNetworkDestinationTag = selectedNetwork?.details;
		}
		this.shouldDisplayDestinationTag = !!selectedNetworkDestinationTag;
		if (this.shouldDisplayDestinationTag) {
			let tag: string;
			switch (selectedNetworkDestinationTag) {
				case "message":
					tag = "message_tag";
					break;
				case "memo":
					tag = "stellar_memo";
					break;
				default:
					tag = "destination_tag";
			}
			this.destinationTagText = this.translationService.translate(tag);
			this.destinationTagTextOptional = this.translationService.translate("destination_tag_optional", [
				this.destinationTagText
			]);
		}
	}

	private handleLegacyMode(wallet: AddressBookWalletModel): void {
		if (wallet?.asset) {
			this.walletFacade.getCoinByCryptocoinName(wallet.asset.name).subscribe((coin) => {
				this.asset.setValue(coin?.id);
				this.asset.disable();
				if (wallet?.network) {
					const coinNetwork = coin.networks.find((item) => item.name.toString() === wallet.network.name);
					this.network.setValue(coinNetwork.coin_network_id);
					this.network.disable();
				}
			});
		}
		if (wallet?.wallet_address) {
			this.walletAddress.setValue(wallet.wallet_address);
			this.walletAddress.disable();
		}
		if (wallet?.wallet_destination_tag) {
			this.destinationTag.setValue(wallet.wallet_destination_tag);
		}
		this.destinationTag.disable();
		this.walletOwner.setValue(wallet.owner_legal_name);
		this.modalTitle = "edit_wallet";
		this.modalDescription = "complete_missing_details";
		this.walletId = wallet.id;
	}

	private handleNewMode(): void {
		this.modalTitle = "new_wallet";
		this.modalDescription = "save_wallet_for_future_use";
	}

	private handleWithdrawMode(wallet: AddressBookWalletModel): void {
		if (wallet.network.destination_tag) {
			this.handleDestinationTagDisplay(null, wallet.network.destination_tag.type);
		}
		if (wallet?.asset) {
			this.asset.setValue(wallet.asset.id);
			this.selectedCryptocoin = wallet.asset;
		}
		if (wallet?.network) {
			this.network.setValue(wallet.network.coin_network_id);
			this.selectedNetwork = wallet.network;
		}
		this.modalTitle = "create_new_wallet";
		this.modalDescription = "create_new_wallet_withdraw_details";
	}

	private handleAddingNewWallet(): void {
		this.segmentService.track({
			name: "Address Book Add Wallet Next Button Tapped"
		});
		this.rudderstackService.track({
			name: "Address Book Add Wallet Next Button Tapped"
		});

		const addNewWalletPayload: AddressBookWalletPayload = {
			coin_network_id: this.network.value,
			wallet_address: this.walletAddress.value,
			user_owned: this.ownWallet.value,
			self_declared: this.selfDeclared.value,
			wallet_host_id: this.selectedWalletHostId,
			...(this.walletHost.value !== AddressBookWalletHostExtra.SELF
				? {
						wallet_host_hint:
							this.walletHost.value === AddressBookWalletHostExtra.OTHER
								? this.otherHostName.value
								: null,
						wallet_host_type: AddressBookWalletHostType.VASP
					}
				: {
						wallet_host_hint: null,
						wallet_host_type: AddressBookWalletHostType.SELF_HOSTED
					}),
			...(!this.ownWallet.value && { owner_legal_name: this.walletOwner.value }),
			...(this.modalType === AddressBookWalletViewType.LEGACY && {
				wallet_host_name:
					this.walletHost.value !== AddressBookWalletHostExtra.OTHER ? this.walletHost.value : null
			}),
			...(this.shouldDisplayDestinationTag &&
				this.destinationTag.value && { wallet_destination_tag: this.destinationTag.value })
		};

		if (
			this.modalType === AddressBookWalletViewType.LEGACY ||
			(this.modalType === AddressBookWalletViewType.WITHDRAW && this.isOriginalWalletDraft)
		) {
			this.addNewWalletSubscription = this.walletService
				.editLegacyWallet(this.walletId, addNewWalletPayload)
				.pipe(
					catchError((err) => {
						const { title, code, field } = err.responseErrors[0].meta[0];
						const errorMessage: string = title;
						this.infoService.setError(errorMessage);
						this.handleInlineError(code, field);
						return EMPTY;
					})
				)
				.subscribe((createdWallet: AddressBookWalletModel) => {
					if (createdWallet.user_owned && !createdWallet.owner_legal_name) {
						createdWallet = this.walletFacade.updateOwnerName(createdWallet);
					}
					if (this.isOriginalWalletDraft) {
						this.walletFacade.validateWalletToSend(
							this.accountWalletId,
							createdWallet,
							this.withdrawSelectedNetwork,
							this.withdrawSelectedCryptocoin
						);
					} else {
						this.walletFacade.displayViewWalletModal(createdWallet);
					}
					this.closeAddNewWalletModal();
				});
		} else {
			this.addNewWalletSubscription = this.walletService
				.addNewWallet(addNewWalletPayload)
				.pipe(
					catchError((err) => {
						const { title, code, field } = err.responseErrors[0].meta[0];
						const errorMessage: string = title;
						this.infoService.setError(errorMessage);
						this.handleInlineError(code, field);
						return EMPTY;
					})
				)
				.subscribe((createdWallet: AddressBookWalletModel) => {
					if (createdWallet.user_owned && !createdWallet.owner_legal_name) {
						createdWallet = this.walletFacade.updateOwnerName(createdWallet);
					}
					if (this.modalType !== AddressBookWalletViewType.WITHDRAW) {
						this.walletFacade.displayViewWalletModal(createdWallet);
					} else {
						this.walletFacade.validateWalletToSend(
							this.accountWalletId,
							createdWallet,
							this.withdrawSelectedNetwork,
							this.withdrawSelectedCryptocoin
						);
					}
					this.closeAddNewWalletModal();
				});
		}
	}

	private handleInlineError(errorCode: string, field: string): void {
		if (this.shouldDisplayEmptyTagInfo) {
			this.shouldDisplayEmptyTagInfo = false;
		}
		const errorMapper: Map<string, string> = new Map([
			["validation.exception.wallet_address.characters", "address_book_validation_address_invalid_characters"],
			["validation.exception.wallet_address.length", "address_book_validation_address_too_many"],
			["validation.exception.wallet_address.address_invalid", "address_book_validation_address_invalid"],
			["validation.exception.wallet_address.not_unique", "address_book_validation_address_already_saved"],
			["validation.exception.wallet_host_hint.length", "address_book_validation_hint_too_many"],
			["validation.exception.owner_legal_name.length", "address_book_validation_owner_name_too_many"],
			["validation.exception.wallet_destination_tag.invalid", "address_book_validation_destination_tag_invalid"]
		]);

		if (field === "wallet_address") {
			this.walletAddress.setErrors({ invalid_address: true });
			this.walletAddressError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_address_generic");
		}

		if (field === "wallet_host_hint") {
			this.otherHostName.setErrors({ invalid_host_hint: true });
			this.hostHintError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_hint_generic");
		}

		if (field === "owner_legal_name") {
			this.walletOwner.setErrors({ invalid_owner_name: true });
			this.walletOwnerError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode))
				: this.translationService.translate("address_book_validation_owner_name_generic");
		}

		if (field === "wallet_destination_tag") {
			this.destinationTag.setErrors({ invalid_destination_tag: true });
			this.destinationTagError = errorMapper.get(errorCode)
				? this.translationService.translate(errorMapper.get(errorCode), [this.destinationTagText])
				: this.translationService.translate("address_book_validation_destination_tag_generic", [
						this.destinationTagText
					]);
		}
	}

	private closeAddNewWalletModal(): void {
		this.infoService.setSuccess(this.translationService.translate("wallet_saved"));
		this.walletService.shouldRefreshWallets.next(true);
		this.modalService.pop();
	}

	ngOnDestroy(): void {
		this.profileNameSubscription.unsubscribe();
		this.addNewWalletSubscription.unsubscribe();
		this.filterHostsSubscription.unsubscribe();
	}
}
